import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { subDays, format, parseISO } from 'date-fns';
import { useLayout } from './LayoutContext';
import { mapFarms } from '../utils/mapMockData';
import { buildFarmGrouping, computeFieldSplit } from '../utils/areaOverlayUtils';
import {
  TIME_RANGES,
  DEFAULT_ANALYTICS_TIME_RANGE,
  prepareChartHourlySeries,
} from './analyticsTimeRanges';
import { STORAGE_KEYS, readStorageValue, removeStorageValue, writeStorageValue } from '../utils/storageKeys';

export {
  TIME_RANGES,
  DEFAULT_ANALYTICS_TIME_RANGE,
  MAX_HOURLY_POINTS,
  downsampleHourlySeries,
  prepareChartHourlySeries,
} from './analyticsTimeRanges';

const AnalyticsContext = createContext(undefined);

const getSessionStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  return window.sessionStorage;
};

const readSelectionFromSession = (farm) => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const stored = readStorageValue(storage, STORAGE_KEYS.analyticsSelection);
  if (!stored?.value) return null;

  try {
    const parsed = JSON.parse(stored.value);
    if (!parsed || typeof parsed !== 'object') return null;

    if (farm?.id && parsed.farmId && parsed.farmId !== farm.id) {
      return null;
    }

    const selectAllSections = typeof parsed.selectAllSections === 'boolean' ? parsed.selectAllSections : false;
    let selectedSections = Array.isArray(parsed.selectedSections) ? parsed.selectedSections : [];

    if (farm?.sections?.length) {
      const grouping = buildFarmGrouping({
        farm,
        sections: farm.sections,
        stations: farm.stations || [],
      });
      const validIds = new Set(grouping.sections.map(section => section.id));
      selectedSections = selectedSections.filter(id => validIds.has(id));

      if (selectAllSections) {
        selectedSections = grouping.sections.map(section => section.id);
      }
    }

    return { selectedSections, selectAllSections };
  } catch (error) {
    console.error('Failed to read analytics selection from sessionStorage:', error);
    storage.removeItem(ANALYTICS_SELECTION_STORAGE_KEY);
    return null;
  }
};

const writeSelectionToSession = (farmId, selectedSections, selectAllSections) => {
  const storage = getSessionStorage();
  if (!storage || !farmId) return;

  const payload = {
    farmId,
    selectedSections,
    selectAllSections,
  };

  try {
    writeStorageValue(storage, STORAGE_KEYS.analyticsSelection, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to write analytics selection to sessionStorage:', error);
  }
};

const clearSelectionFromSession = () => {
  const storage = getSessionStorage();
  if (!storage) return;
  removeStorageValue(storage, STORAGE_KEYS.analyticsSelection);
};

// Helper function to create sections (2 main groups per field: Section A / Section B)
const createSectionsFromProject = (project) => {
  if (!project || !project.stations) return [];
  
  if (project.sections && project.sections.length > 0) {
    const centroid = (poly) => {
      if (!poly || poly.length === 0) return [0, 0];
      const lat = poly.reduce((sum, p) => sum + p[0], 0) / poly.length;
      const lon = poly.reduce((sum, p) => sum + p[1], 0) / poly.length;
      return [lat, lon];
    };

    return project.sections.map((section, index) => {
      const sectionId = section.id || `${project.id}-section-${index + 1}`;
      const center = Array.isArray(section.center) ? section.center : centroid(section.polygon);
      const stationIds = project.stations
        .filter(station => station.sectionId === sectionId)
        .map(station => station.id);

      return {
        id: sectionId,
        name: section.name || sectionId,
        position: { lat: center[0], lon: center[1] },
        stationIds,
        areaHa: section.areaHa,
        polygon: section.polygon,
        locationDescription: section.name || sectionId,
      };
    });
  }

  // If we have a farm polygon + at least 4 stations, split into two equal-ish halves.
  if (project.polygon && project.stations.length >= 4) {
    const split = computeFieldSplit(project.polygon, project.stations);
    if (split) {
      const { axis, threshold, polyA, polyB, areaA, areaB } = split;
      const getCoord = (s) => (axis === 'lon' ? s.locationLon : s.locationLat);
      const sorted = [...project.stations].sort((a, b) => getCoord(a) - getCoord(b));
      const aSet = new Set(sorted.slice(0, 2).map(s => s.id));

      const stationIdsA = project.stations.filter(s => aSet.has(s.id)).map(s => s.id);
      const stationIdsB = project.stations.filter(s => !aSet.has(s.id)).map(s => s.id);

      const centroid = (poly) => {
        if (!poly || poly.length === 0) return { lat: 0, lon: 0 };
        const lat = poly.reduce((sum, p) => sum + p[0], 0) / poly.length;
        const lon = poly.reduce((sum, p) => sum + p[1], 0) / poly.length;
        return { lat, lon };
      };

      return [
        {
          id: `${project.id}-section-a`,
          name: 'Section A',
          position: centroid(polyA),
          stationIds: stationIdsA,
          areaHa: areaA / 10000,
          polygon: polyA,
          locationDescription: 'Section A',
        },
        {
          id: `${project.id}-section-b`,
          name: 'Section B',
          position: centroid(polyB),
          stationIds: stationIdsB,
          areaHa: areaB / 10000,
          polygon: polyB,
          locationDescription: 'Section B',
        }
      ];
    }
  }

  // Fallback: keep legacy station-based sections if split isn't possible.
  return project.stations.map((station, index) => ({
    id: station.id,
    name: station.locationDescription || (station.isMaster ? 'Master Station' : `Section ${String.fromCharCode(65 + index)}`),
    stationCode: station.stationCode,
    sensorType: station.sensorType,
    isMaster: station.isMaster,
    position: { lat: station.locationLat, lon: station.locationLon },
    status: station.status,
    locationDescription: station.locationDescription,
  }));
};

// Build FARMS from real project data with actual GPS coordinates
const FARMS = mapFarms.map(project => ({
  id: project.id,
  name: project.name || project.title,
  title: project.title,
  location: project.location,
  centerCoordinates: project.centerCoordinates,
  center: project.center,
  zoom: project.zoom,
  polygon: project.polygon,
  bounds: project.bounds,
  masterStation: project.masterStation,
  slaveStations: project.slaveStations,
  stations: project.stations,
  sections: createSectionsFromProject(project),
  excludedStationNumbers: project.excludedStationNumbers,
  // Metadata
  status: project.status,
  power: project.power,
  area: project.area,
  crop: project.crop,
  technology: project.technology,
  description: project.description,
  totalStations: project.totalStations,
  activeStations: project.activeStations,
}));

// Legacy station pairs for backward compatibility
const STATION_PAIRS = [
  { id: 'pair-001', location: 'A1_Lazio - Block 001', campo_id: 'A1-LAZIO-CAMPO-001', retro_id: 'A1-LAZIO-RETRO-001' },
  { id: 'pair-002', location: 'A2_Umbria - Block 001', campo_id: 'A2-UMBRIA-CAMPO-001', retro_id: 'A2-UMBRIA-RETRO-001' },
  { id: 'pair-003', location: 'A3_Emilia - Block 001', campo_id: 'A3-EMILIA-CAMPO-001', retro_id: 'A3-EMILIA-RETRO-001' },
  { id: 'pair-004', location: 'A3_Emilia - Block 010', campo_id: 'A3-EMILIA-CAMPO-010', retro_id: 'A3-EMILIA-RETRO-010' },
];

const deterministicFraction = (seed, salt = 0) => {
  const input = `${seed}:${salt}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
};

const deterministicSigned = (seed, salt = 0) => (deterministicFraction(seed, salt) * 2) - 1;

// Mock data generator for 15-minute interval time series
function generateHourlyData(stationId, days) {
  const data = [];
  const now = new Date();
  const intervals = days * 24 * 4; // 15-minute intervals

  // Station-specific offsets
  const isField = stationId === 'FIELD';
  const tempOffset = isField ? 2 : -1; // FIELD warmer, UNDER cooler
  const rhOffset = isField ? -8 : 5; // FIELD drier, UNDER moister
  const ghiMultiplier = isField ? 1.0 : 0.65; // UNDER gets 65% radiation (shade)

  for (let i = intervals; i >= 0; i--) {
    const timestamp = new Date(now.getTime() - i * 15 * 60 * 1000);
    const hour = timestamp.getHours();
    const dayOfYear = Math.floor(timestamp.getTime() / (1000 * 60 * 60 * 24));
    const seed = `${stationId}-${i}`;

    // Diurnal temperature pattern (peaks at 14:00)
    const tempBase = 22 + 10 * Math.sin(((hour - 6) / 12) * Math.PI);
    const tempNoise = deterministicSigned(seed, 1) * 1.1;
    const t_c = Math.max(15, Math.min(38, tempBase + tempOffset + tempNoise));

    // Relative humidity (inversely related to temperature)
    const rhBase = 70 - 20 * Math.sin(((hour - 6) / 12) * Math.PI);
    const rhNoise = deterministicSigned(seed, 2) * 3.2;
    const rh_pct = Math.max(35, Math.min(95, rhBase + rhOffset + rhNoise));

    // Dew point calculation (simplified)
    const dewpoint_c = t_c - ((100 - rh_pct) / 5);

    // VPD calculation (simplified, kPa)
    const vpd_kpa = Math.max(0.1, (1 - rh_pct / 100) * (0.6108 * Math.exp((17.27 * t_c) / (t_c + 237.3))));

    // Wind speed (typically higher during day)
    const windBase = 3 + 2 * Math.sin(((hour - 10) / 12) * Math.PI);
    const wind_ms = Math.max(0, windBase + deterministicSigned(seed, 3) * 1.4);
    const wind_gust_ms = wind_ms * (1.2 + deterministicFraction(seed, 4) * 0.3);
    const wind_dir_deg = Math.floor(deterministicFraction(seed, 5) * 360);

    // Solar radiation (GHI) - zero at night, peaks at noon
    let ghi_wm2 = 0;
    if (hour >= 6 && hour <= 18) {
      const solarBase = 900 * Math.sin(((hour - 6) / 12) * Math.PI);
      const cloudiness = 0.8 + deterministicFraction(seed, 6) * 0.2; // 80-100% clear
      ghi_wm2 = Math.max(0, solarBase * cloudiness * ghiMultiplier);
    }

    // Rain (random events, more likely in afternoon)
    const rainChance = deterministicFraction(seed, 7);
    const rain_mm = rainChance < 0.02 ? deterministicFraction(seed, 8) * 5 : 0;

    // Soil moisture (relatively stable, slowly decreasing without rain)
    const vwc_base_10cm = 30 - (dayOfYear % 10) * 0.5 + (rain_mm > 0 ? 5 : 0);
    const vwc_base_30cm = 35 - (dayOfYear % 10) * 0.3 + (rain_mm > 0 ? 3 : 0);
    const vwc_base_50cm = 38 - (dayOfYear % 10) * 0.2 + (rain_mm > 0 ? 2 : 0);
    const soil_vwc_pct_10cm = Math.max(15, Math.min(45, vwc_base_10cm + deterministicSigned(seed, 9) * 1.8 + (isField ? 0 : 4)));
    const soil_vwc_pct_30cm = Math.max(20, Math.min(50, vwc_base_30cm + deterministicSigned(seed, 10) * 1.8 + (isField ? 0 : 3)));
    const soil_vwc_pct_50cm = Math.max(25, Math.min(52, vwc_base_50cm + deterministicSigned(seed, 11) * 1.3 + (isField ? 0 : 2)));

    // Soil temperature (follows air temp with lag and damping)
    const soil_temp_c_10cm = t_c * 0.9 + deterministicSigned(seed, 12) * 0.7;
    const soil_temp_c_30cm = t_c * 0.85 + deterministicSigned(seed, 13) * 0.4;

    // Soil pH (relatively stable, slightly affected by moisture)
    const soil_ph_base = 6.7 + (isField ? -0.05 : 0.1);
    const soil_ph = Math.max(5.5, Math.min(8.0, soil_ph_base + deterministicSigned(seed, 14) * 0.2));

    // Electrical Conductivity (dS/m) - affected by moisture and temperature
    const soil_ec_base = 1.2 + (isField ? -0.1 : 0.15);
    const soil_ec_dsm = Math.max(0.3, Math.min(2.5, soil_ec_base + deterministicSigned(seed, 15) * 0.3));

    // Atmospheric Pressure (hPa) - same for both stations, varies with weather
    const pressure_base = 1013.25 + Math.sin(dayOfYear / 7) * 8;
    const pressure_hpa = pressure_base + deterministicSigned(seed, 16) * 2.2;

    // Battery voltage (stable around 12.5V)
    const battery_v = 12.3 + deterministicFraction(seed, 17) * 0.6;

    data.push({
      ts_iso: timestamp.toISOString(),
      station_id: stationId,
      t_c: parseFloat(t_c.toFixed(1)),
      rh_pct: parseFloat(rh_pct.toFixed(1)),
      dewpoint_c: parseFloat(dewpoint_c.toFixed(1)),
      vpd_kpa: parseFloat(vpd_kpa.toFixed(2)),
      wind_ms: parseFloat(wind_ms.toFixed(1)),
      wind_gust_ms: parseFloat(wind_gust_ms.toFixed(1)),
      wind_dir_deg,
      ghi_wm2: parseFloat(ghi_wm2.toFixed(0)),
      rain_mm: parseFloat(rain_mm.toFixed(1)),
      soil_vwc_pct_10cm: parseFloat(soil_vwc_pct_10cm.toFixed(1)),
      soil_vwc_pct_30cm: parseFloat(soil_vwc_pct_30cm.toFixed(1)),
      soil_vwc_pct_50cm: parseFloat(soil_vwc_pct_50cm.toFixed(1)),
      soil_temp_c_10cm: parseFloat(soil_temp_c_10cm.toFixed(1)),
      soil_temp_c_30cm: parseFloat(soil_temp_c_30cm.toFixed(1)),
      soil_ph: parseFloat(soil_ph.toFixed(2)),
      soil_ec_dsm: parseFloat(soil_ec_dsm.toFixed(2)),
      pressure_hpa: parseFloat(pressure_hpa.toFixed(1)),
      battery_v: parseFloat(battery_v.toFixed(2)),
    });
  }

  return data;
}

// Mock data generator for daily aggregates
function generateDailyData(hourlyData, stationId) {
  const dailyMap = {};

  // Group by date
  hourlyData.forEach((record) => {
    const date = format(parseISO(record.ts_iso), 'yyyy-MM-dd');
    if (!dailyMap[date]) {
      dailyMap[date] = [];
    }
    dailyMap[date].push(record);
  });

  // Calculate daily aggregates
  return Object.keys(dailyMap).map((date) => {
    const dayRecords = dailyMap[date];
    const temps = dayRecords.map((r) => r.t_c);
    const rhs = dayRecords.map((r) => r.rh_pct);
    const winds = dayRecords.map((r) => r.wind_ms);
    const ghis = dayRecords.map((r) => r.ghi_wm2);
    const rains = dayRecords.map((r) => r.rain_mm);

    const tmin_c = Math.min(...temps);
    const tmax_c = Math.max(...temps);
    const tmean_c = temps.reduce((a, b) => a + b, 0) / temps.length;
    const rh_mean_pct = rhs.reduce((a, b) => a + b, 0) / rhs.length;
    const wind_u2_ms_mean = winds.reduce((a, b) => a + b, 0) / winds.length;
    const rain_mm = rains.reduce((a, b) => a + b, 0);

    // Insolation (kWh/m²) = sum of GHI over day / 1000 * (15/60) hours per interval
    const insolation_kwh_m2 = (ghis.reduce((a, b) => a + b, 0) / 1000) * 0.25;

    // Peak Sun Hours ≈ daily insolation
    const psh = insolation_kwh_m2;

    // Reference ET (simplified Penman-Monteith approximation)
    const eto_mm = 0.0023 * (tmean_c + 17.8) * Math.sqrt(tmax_c - tmin_c) * 0.408;

    // Crop ET (assuming Kc = 0.85 for agrivoltaics)
    const etc_mm = eto_mm * 0.85;

    return {
      date,
      station_id: stationId,
      tmin_c: parseFloat(tmin_c.toFixed(1)),
      tmax_c: parseFloat(tmax_c.toFixed(1)),
      tmean_c: parseFloat(tmean_c.toFixed(1)),
      rh_mean_pct: parseFloat(rh_mean_pct.toFixed(1)),
      wind_u2_ms_mean: parseFloat(wind_u2_ms_mean.toFixed(1)),
      insolation_kwh_m2: parseFloat(insolation_kwh_m2.toFixed(2)),
      psh: parseFloat(psh.toFixed(2)),
      rain_mm: parseFloat(rain_mm.toFixed(1)),
      eto_mm: parseFloat(eto_mm.toFixed(2)),
      etc_mm: parseFloat(etc_mm.toFixed(2)),
    };
  });
}

export function AnalyticsProvider({ children }) {
  // Get selected farm from LayoutContext (set in sidebar)
  const { selectedFarm: layoutFarm } = useLayout();
  
  // Map layout farm to analytics farm structure (with sections and GPS coordinates)
  // The layoutFarm and FARMS now use the same project IDs from mapMockData
  const selectedFarm = useMemo(() => {
    if (!layoutFarm) return FARMS[0];
    const farm = FARMS.find(f => f.id === layoutFarm.id);
    return farm || FARMS[0];
  }, [layoutFarm?.id]);

  const initialSelectionRef = useRef(null);
  if (initialSelectionRef.current === null) {
    initialSelectionRef.current = readSelectionFromSession(selectedFarm) || {
      selectedSections: [],
      selectAllSections: false,
    };
  }

  const [selectedSections, setSelectedSections] = useState(initialSelectionRef.current.selectedSections); // Array of section IDs
  const [selectAllSections, setSelectAllSections] = useState(initialSelectionRef.current.selectAllSections);
  const [drillState, setDrillState] = useState(null);
  const [selectedPair, setSelectedPair] = useState(STATION_PAIRS[0]); // Legacy support
  const [timeRange, setTimeRange] = useState(DEFAULT_ANALYTICS_TIME_RANGE); // Default to 14 days
  const [loading, setLoading] = useState(true);
  const [hourlyData, setHourlyData] = useState({ FIELD: [], UNDER: [] });
  const [dailyData, setDailyData] = useState({ FIELD: [], UNDER: [] });

  // Reset selection when farm changes
  const prevFarmIdRef = useRef(selectedFarm?.id);
  useEffect(() => {
    if (!selectedFarm?.id) return;

    if (prevFarmIdRef.current && prevFarmIdRef.current !== selectedFarm.id) {
      clearSelectionFromSession();
      setSelectedSections([]);
      setSelectAllSections(false);
      setDrillState(null);
    }

    prevFarmIdRef.current = selectedFarm.id;
  }, [selectedFarm?.id]);

  // Update sections when selectAllSections is toggled (only when toggling, not when farm changes)
  const prevSelectAllRef = useRef(selectAllSections);
  useEffect(() => {
    if (selectedFarm && selectAllSections && !prevSelectAllRef.current) {
      // Only update if we're enabling "select all" (was false, now true)
      const grouping = buildFarmGrouping({
        farm: selectedFarm,
        sections: selectedFarm.sections,
        stations: selectedFarm.stations || [],
      });
      const allSectionIds = grouping.sections.map(s => s.id);
      setSelectedSections(allSectionIds);
    }
    prevSelectAllRef.current = selectAllSections;
  }, [selectAllSections, selectedFarm]);

  // Persist selection in sessionStorage for the current session
  useEffect(() => {
    if (!selectedFarm?.id) return;
    writeSelectionToSession(selectedFarm.id, selectedSections, selectAllSections);
  }, [selectedFarm?.id, selectedSections, selectAllSections]);

  // Generate mock data when sections or time range changes
  useEffect(() => {
    setLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      // For now, aggregate data from all selected sections
      // In a real implementation, this would fetch data for each selected section and aggregate
      const fieldHourlyRaw = generateHourlyData('FIELD', timeRange.days);
      const underHourlyRaw = generateHourlyData('UNDER', timeRange.days);

      const fieldDaily = generateDailyData(fieldHourlyRaw, 'FIELD');
      const underDaily = generateDailyData(underHourlyRaw, 'UNDER');

      const fieldHourly = prepareChartHourlySeries(fieldHourlyRaw, timeRange.days);
      const underHourly = prepareChartHourlySeries(underHourlyRaw, timeRange.days);

      setHourlyData({ FIELD: fieldHourly, UNDER: underHourly });
      setDailyData({ FIELD: fieldDaily, UNDER: underDaily });
      setLoading(false);
    }, 800);
  }, [selectedSections, selectAllSections, timeRange]);

  // Calculate hero metrics
  const heroMetrics = useMemo(() => {
    if (loading || dailyData.FIELD.length === 0) {
      return null;
    }

    const fieldRecent = dailyData.FIELD[dailyData.FIELD.length - 1];
    const underRecent = dailyData.UNDER[dailyData.UNDER.length - 1];

    // Average GHI
    const avgGhiField = dailyData.FIELD.reduce((sum, d) => sum + d.insolation_kwh_m2, 0) / dailyData.FIELD.length;
    const avgGhiUnder = dailyData.UNDER.reduce((sum, d) => sum + d.insolation_kwh_m2, 0) / dailyData.UNDER.length;
    const ghiDelta = ((avgGhiUnder / avgGhiField - 1) * 100).toFixed(1);

    // Average temperature
    const avgTempField = dailyData.FIELD.reduce((sum, d) => sum + d.tmean_c, 0) / dailyData.FIELD.length;
    const avgTempUnder = dailyData.UNDER.reduce((sum, d) => sum + d.tmean_c, 0) / dailyData.UNDER.length;
    const tempDelta = (avgTempUnder - avgTempField).toFixed(1);

    // Total ET
    const totalEtcField = dailyData.FIELD.reduce((sum, d) => sum + d.etc_mm, 0);
    const totalEtcUnder = dailyData.UNDER.reduce((sum, d) => sum + d.etc_mm, 0);
    const etcDelta = ((totalEtcUnder / totalEtcField - 1) * 100).toFixed(1);

    // Total rain
    const totalRainField = dailyData.FIELD.reduce((sum, d) => sum + d.rain_mm, 0);
    const totalRainUnder = dailyData.UNDER.reduce((sum, d) => sum + d.rain_mm, 0);

    // Average VPD
    const avgVpdField = hourlyData.FIELD.reduce((sum, h) => sum + h.vpd_kpa, 0) / hourlyData.FIELD.length;
    const avgVpdUnder = hourlyData.UNDER.reduce((sum, h) => sum + h.vpd_kpa, 0) / hourlyData.UNDER.length;
    const vpdDelta = ((avgVpdUnder / avgVpdField - 1) * 100).toFixed(1);

    return {
      avgGhiField: avgGhiField.toFixed(2),
      avgGhiUnder: avgGhiUnder.toFixed(2),
      ghiDelta,
      avgTempField: avgTempField.toFixed(1),
      avgTempUnder: avgTempUnder.toFixed(1),
      tempDelta,
      totalEtcField: totalEtcField.toFixed(1),
      totalEtcUnder: totalEtcUnder.toFixed(1),
      etcDelta,
      totalRainField: totalRainField.toFixed(1),
      totalRainUnder: totalRainUnder.toFixed(1),
      avgVpdField: avgVpdField.toFixed(2),
      avgVpdUnder: avgVpdUnder.toFixed(2),
      vpdDelta,
    };
  }, [loading, dailyData, hourlyData]);

  const value = {
    // State
    selectedFarm,
    selectedSections,
    selectAllSections,
    drillState,
    selectedPair, // Legacy
    timeRange,
    loading,
    hourlyData,
    dailyData,
    heroMetrics,

    // Constants
    FARMS,
    STATION_PAIRS,
    TIME_RANGES,

    // Actions
    setSelectedSections,
    setSelectAllSections,
    setDrillState,
    setSelectedPair, // Legacy
    setTimeRange,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

AnalyticsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}
